import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import ReactCrop, {
  centerCrop,
  convertToPixelCrop,
  makeAspectCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useLocation, useNavigate } from "react-router-dom";

import { CallAPI } from "../../utils/apiActions";
import {
  BOT_APPEARANCE,
  BOT_APPEARANCE_DETAILS,
  BOT_CHAT_ICON,
  BOT_CHAT_ICON_REMOVE,
} from "../../utils/APIEndPoints";
import { showToast } from "../../helper/toastHelper";
import { LABEL } from "../../utils/constant";
import Swal from "sweetalert2";
import usePermissions from "../../hooks/usePermissions";
import ColorPickerField from "../ColorPickerField";
import SplashScreen from "../SplashScreen";
import { useCallAPI } from "../../hooks/useCallAPI";

const Templates = lazy(() => import("./Templates/Templates"));
let colorArrInitialState = [
  {
    key: "header_bg",
    lable: "Header Background",
    color: "#4F8ED2",
    elementClassName: "header-back-color",
    props: "background-color",
  },
  {
    key: "page_bg",
    lable: "Page Background",
    color: "#ffffff",
    elementClassName: "page-back-color",
    props: "background-color",
  },
  {
    key: "bot_tb_color",
    lable: "Bot Text Box Color",
    color: "#C1E2FC",
    elementClassName: "bot-color-box",
    props: "background-color",
  },
  {
    key: "bot_border_color",
    lable: "Bot Text Border Color",
    color: "#C1D3EE",
    elementClassName: "bot-color-border",
    props: "border",
  },
  {
    key: "bot_txt_color",
    lable: "Bot Text Color",
    color: "#000000",
    elementClassName: "bot-text-color",
    props: "color",
  },
  {
    key: "sender_tb_color",
    lable: "Sender Text box Color",
    color: "#C2D7EF",
    elementClassName: "sender-color-box",
    props: "background-color",
  },
  {
    key: "sender_border_color",
    lable: "Sender Text Border Color",
    color: "#C1D3EE",
    elementClassName: "sender-color-border",
    props: "border",
  },
  {
    key: "sender_txt_color",
    lable: "Sender Text Color",
    color: "#000000",
    elementClassName: "sender-text-color",
    props: "color",
  },
  {
    key: "link_color",
    lable: "Link Color",
    color: "#265EA8",
    elementClassName: "link-color",
    props: "color",
  },
  {
    key: "icon_color",
    lable: "Icon Color",
    color: "#4469D2",
    elementClassName: "icon-color",
    props: "color",
  },
  {
    key: "option_tb_color",
    lable: "Option Button Color",
    color: "#1D59A6",
    elementClassName: "option-color",
    props: "background",
  },
  {
    key: "option_txt_color",
    lable: "Option Text Color",
    color: "#ffffff",
    elementClassName: "option-color",
    props: "color",
  },
  {
    key: "footer_bg",
    lable: "Footer Background",
    color: "#E6EDF2",
    elementClassName: "footer-back-color",
    props: "background-color",
  },
  {
    key: "btn_color",
    lable: "Button Color",
    color: "#A1CBFA",
    elementClassName: "button-color",
    props: "background-color",
  },
  {
    key: "btn_txt_color",
    lable: "Button Text Color",
    color: "#020619",
    elementClassName: "button-text-color",
    props: "color",
  },
  {
    key: "input_box_color",
    lable: "Message Input Box Color",
    color: "#ffffff",
    elementClassName: "input-box-color",
    props: "background-color",
  },
  {
    key: "input_color",
    lable: "Typing Message Color",
    color: "#040E16",
    elementClassName: "input-color",
    props: "color",
  },
];
/**
 * Component for configuring bot appearance settings.
 * @param {Object} props - Component props.
 * @returns {JSX.Element} The rendered component.
 */
export default function BotAppearance({ ...props }) {
  const supportedImageTypes = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/bmp",
  ];
  const supportedImageExtensions = [
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".webp",
    ".bmp",
  ];
  const botIconUploadErrorMessage =
    "Unsupported image format. Please upload a JPG, JPEG, PNG, GIF, WEBP, or BMP file.";
  const [colorSettingsArray, setColorSettingsArray] =
    useState(colorArrInitialState);
  const [showIndex, setShowIndex] = useState(null);
  const location = useLocation();
  const bot_id = new URLSearchParams(location?.search).get("id") || null;
  const bot_status =
    new URLSearchParams(location?.search).get("status") || null;
  const complainceID =
    new URLSearchParams(location?.search).get("complainceID") || "";

  const fileRef = useRef();
  const cropImageRef = useRef(null);

  const [selectedTemplate, setSelectedTemplate] = useState(
    !!new URLSearchParams(location?.search).get("temp")
      ? `template#${new URLSearchParams(location?.search).get("temp")}`
      : null,
  );
  const [loader, setLoader] = useState(false);
  const [botNameFields, setBotNameFields] = useState({
    isUpdate: false,
    name: null,
    err: null,
  });
  const [botImageFields, setBotImageFields] = useState({
    icon: null,
    err: null,
  });
  const [botIconCrop, setBotIconCrop] = useState();
  const [completedBotIconCrop, setCompletedBotIconCrop] = useState(null);
  const [cropImageData, setCropImageData] = useState({
    src: null,
    file: null,
  });
  const [botLoad, setBotLoad] = useState(false);
  const permission = usePermissions("my-chatbot");

  const {
    data: botStyleRes,
    loading: botStyleLoading,
    revalidate: fetchBotStyle,
  } = useCallAPI({
    end_point: "USER",
    method: "POST",
    apiUrl: BOT_APPEARANCE_DETAILS,
    body: { bot_id },
    enabled: false,
  });

  // const inputRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    colorSettingsArray?.map((settings, index) => {
      const elements = document.querySelectorAll(
        `.${settings.elementClassName}`,
      );
      elements.forEach((el) => {
        el.style.setProperty(
          settings.props,
          settings.props == "border"
            ? `2px solid ${settings.color}`
            : settings.color,
          "important",
        );
      });
    });
  }, [colorSettingsArray]);

  useEffect(() => {
    if (!!selectedTemplate && !!bot_id) {
      fetchBotStyle();
    }
    if (selectedTemplate == null) {
      setColorSettingsArray(colorArrInitialState);
      setBotNameFields({ ...botNameFields, err: null });
      setBotImageFields({ ...botImageFields, err: null });
    }
  }, [selectedTemplate, bot_id]);

  useEffect(() => {
    if (!botStyleRes?.status) return;

    const botData = botStyleRes?.res?.data?.data?.[0];
    if (!botData) return;

    props.setFinalStep(botData.completedStep);

    setBotNameFields((prev) => ({
      ...prev,
      name: botData.bot_name || "",
    }));

    setBotImageFields((prev) => ({
      ...prev,
      icon: botData.bot_image || "",
    }));

    setColorSettingsArray((prev) =>
      prev.map((item) => ({
        ...item,
        color: botData.styles?.[item.key] ?? item.color,
      })),
    );

    localStorage.setItem("botStyle", JSON.stringify(botData.styles || {}));
  }, [botStyleRes]);

  console.log(botStyleRes);

  // useEffect(() => {
  //   if (!botStyleRes?.status || !botStyleRes?.res) return;
  //   if (
  //     !Array.isArray(botStyleRes.res.data.data[0]) ||
  //     botStyleRes.res.data.data[0].length == 0
  //   ) {
  //     return;
  //   }
  //   console.log(botStyleRes.res.data.data, "botStyleRes.res.data");
  //   const botData = JSON.parse(JSON.stringify(botStyleRes.res.data.data[0]));
  //   if (!botData) return;
  //   props.setFinalStep(botData.completedStep);

  //   setBotNameFields((p) => ({ ...p, name: botData.bot_name || "" }));
  //   setBotImageFields((p) => ({ ...p, icon: botData.bot_image || "" }));

  //   const styleArr = [...colorSettingsArray];
  //   Object.entries(botData.styles || {}).forEach(([key, value]) => {
  //     const index = styleArr.findIndex((x) => x.key === key);
  //     if (index !== -1) styleArr[index].color = value;
  //   });

  //   setColorSettingsArray(styleArr);
  //   localStorage.setItem("botStyle", JSON.stringify(botData.styles));
  // }, [botStyleRes]);

  useEffect(() => {
    if (complainceID) {
      const complianceKey = `compliance_alert_shown_${complainceID}`;
      const isShown = localStorage.getItem(complianceKey);

      if (!isShown) {
        Swal.fire({
          title: "Complete Required Step",
          html: '<p style="font-weight: 400;">To continue, please complete the Appearance step. Bot metadata is only stored after this step is finished.</p>',
          icon: "info",
          confirmButtonText: "OK",
        });

        localStorage.setItem(complianceKey, "true");
      }
    }
  }, [complainceID]);

  useEffect(() => {
    return () => {
      if (cropImageData.src) {
        URL.revokeObjectURL(cropImageData.src);
      }
    };
  }, [cropImageData.src]);

  const botIconPreviewSrc = useMemo(() => {
    if (!botImageFields.icon) {
      return "https://mdbcdn.b-cdn.net/img/Photos/new-templates/bootstrap-chat/ava1-bg.webp";
    }

    if (botImageFields.icon instanceof File) {
      return URL.createObjectURL(botImageFields.icon);
    }

    return botImageFields.icon;
  }, [botImageFields.icon]);

  useEffect(() => {
    return () => {
      if (botImageFields.icon instanceof File && botIconPreviewSrc) {
        URL.revokeObjectURL(botIconPreviewSrc);
      }
    };
  }, [botImageFields.icon, botIconPreviewSrc]);

  // const getBotStyle = async () => {
  //   let styleArr = [...colorSettingsArray];
  //   let body = {
  //     bot_id: bot_id,
  //   };
  //   const response = await CallAPI(
  //     "USER",
  //     "POST",
  //     BOT_APPEARANCE_DETAILS,
  //     body,
  //   );
  //   props.setFinalStep(response?.res.data?.data[0].completedStep);
  //   setBotNameFields({
  //     ...botNameFields,
  //     name: response?.res.data?.data[0].bot_name || "test",
  //   });
  //   setBotImageFields({
  //     ...botImageFields,
  //     icon: response?.res.data?.data[0]?.bot_image || "",
  //   });
  //   setBotLoad(true);
  //   localStorage.setItem(
  //     "botStyle",
  //     JSON.stringify(response?.res.data?.data[0].styles),
  //   );
  //   for (let [key, value] of Object.entries(
  //     response?.res.data?.data[0].styles,
  //   )) {
  //     var foundIndex = styleArr.findIndex((x) => x.key == key);
  //     if (foundIndex != -1) styleArr[foundIndex].color = value;
  //   }
  //   setColorSettingsArray(styleArr);
  // };

  /**
   * Converts RGBA color object to Hex string.
   * @param {number} r - Red value.
   * @param {number} g - Green value.
   * @param {number} b - Blue value.
   * @param {number} a - Alpha value.
   * @returns {string} Hex color string.
   */
  const rgbaToHex = (r, g, b, a) => {
    r = Math.min(255, Math.max(0, r));
    g = Math.min(255, Math.max(0, g));
    b = Math.min(255, Math.max(0, b));
    a = Math.min(1, Math.max(0, a));

    const hex = (x) => x.toString(16).padStart(2, "0");
    const alphaHex = Math.round(a * 255)
      .toString(16)
      .padStart(2, "0");

    return `#${hex(r)}${hex(g)}${hex(b)}${alphaHex}`;
  };

  /**
   * Sets the color for a specific setting key.
   * @param {Object} rgba - The RGBA color object.
   * @param {string} key - The key of the setting to update.
   */
  const handleSetColor = (rgba, key) => {
    const hexColor = rgbaToHex(rgba.r, rgba.g, rgba.b, rgba.a);
    let laColorSettings = [...colorSettingsArray];

    var foundIndex = laColorSettings.findIndex((x) => x.key == key);
    laColorSettings[foundIndex].color = hexColor;

    setColorSettingsArray(laColorSettings);
  };

  /**
   * Saves the bot appearance settings (Step 1).
   */
  const handleSaveStep1 = async () => {
    if (loader) return;
    setLoader(true);
    if (!!botNameFields.name) {
      let body = {};
      body.step = 1;
      body.bot_name = botNameFields.name;
      body.styles = {};
      colorSettingsArray.map((colorObj, ind) => {
        body.styles[colorObj.key] = colorObj.color;
      });
      if (!!bot_id) {
        body.bot_id = bot_id;
      }
      if (!!bot_status) {
        body.status = +bot_status;
      } else {
        props.setFinalStep(1);
      }

      body.template_id = selectedTemplate.split("#")[1];
      body.template_name = selectedTemplate;
      if (complainceID) body.complianceCheckId = complainceID; //addcomplaince id for bot create
      localStorage.setItem("botStyle", JSON.stringify(body.styles));
      const response = await CallAPI("USER", "POST", BOT_APPEARANCE, body);
      console.log("response-----", response);
      if (response.status) {
        const hasNewBotIconFile = botImageFields.icon instanceof File;
        const hasExistingBotIcon =
          typeof botImageFields.icon === "string" &&
          botImageFields.icon.trim() !== "";

        if (hasNewBotIconFile) {
          const formData = new FormData();
          formData.append("files", botImageFields.icon);
          formData.append("bot_id", response.res.data.bot_id);
          await CallAPI("USER", "POST", BOT_CHAT_ICON, formData, "file");
        } else if (!hasExistingBotIcon) {
          let url = `${BOT_CHAT_ICON_REMOVE}/${response.res.data.bot_id}`;
          await CallAPI("USER", "POST", url, {});
        }
        if (!bot_id) {
          appendQueryParam({
            id: response?.res.data.bot_id,
            temp: selectedTemplate.split("#")[1],
          });
        }
        props.handleSetTab(
          "2",
          botNameFields.name,
          selectedTemplate.split("#")[1],
        );
        showToast(`Appearance saved successfully.`, LABEL.TOAST_TYPE.SUCCESS);
        setLoader(false);
      }
    } else if (!botNameFields.name) {
      setBotNameFields({ ...botNameFields, err: "Please enter a bot name" });
      setLoader(false);
    }
  };

  /**
   * Resets the bot icon field.
   */
  const resetIcon = (e) => {
    e?.stopPropagation();
    if (fileRef.current) {
      fileRef.current.value = "";
    }
    setBotImageFields({ icon: null, err: null });
  };

  /**
   * Opens the bot image picker through a single click path to avoid
   * duplicate native + programmatic file dialog launches.
   */
  const openBotImagePicker = () => {
    if (!permission.edit || !fileRef.current) {
      return;
    }

    fileRef.current.value = "";
    fileRef.current.click();
  };

  const closeBotIconCrop = () => {
    setCropImageData({ src: null, file: null });
    setBotIconCrop(undefined);
    setCompletedBotIconCrop(null);
  };

  const centerBotIconCrop = (mediaWidth, mediaHeight) =>
    centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 80,
        },
        1,
        mediaWidth,
        mediaHeight,
      ),
      mediaWidth,
      mediaHeight,
    );

  const handleCropImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    const nextCrop = centerBotIconCrop(width, height);

    setBotIconCrop(nextCrop);
    setCompletedBotIconCrop(convertToPixelCrop(nextCrop, width, height));
  };

  const createCroppedImageFile = (image, crop, file) =>
    new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas");
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const cropWidth = Math.max(1, Math.round(crop.width * scaleX));
      const cropHeight = Math.max(1, Math.round(crop.height * scaleY));
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        reject(new Error("Unable to prepare image crop."));
        return;
      }

      canvas.width = cropWidth;
      canvas.height = cropHeight;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        cropWidth,
        cropHeight,
      );

      const outputType =
        file.type === "image/jpeg" || file.type === "image/webp"
          ? file.type
          : "image/png";
      const extension =
        outputType === "image/jpeg" ? "jpg" : outputType.split("/")[1];
      const outputName = `${file.name.replace(/\.[^/.]+$/, "")}-cropped.${extension}`;

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Unable to crop image."));
            return;
          }

          resolve(new File([blob], outputName, { type: outputType }));
        },
        outputType,
        0.5,
      );
    });

  const applyBotIconCrop = async () => {
    if (!cropImageData.file || !cropImageRef.current) {
      return;
    }

    if (!completedBotIconCrop?.width && !botIconCrop) {
      return;
    }

    const crop = completedBotIconCrop?.width
      ? completedBotIconCrop
      : convertToPixelCrop(
          botIconCrop,
          cropImageRef.current.width,
          cropImageRef.current.height,
        );

    try {
      const croppedFile = await createCroppedImageFile(
        cropImageRef.current,
        crop,
        cropImageData.file,
      );

      setBotImageFields((prev) => ({
        ...prev,
        icon: croppedFile,
        err: null,
      }));
      closeBotIconCrop();
    } catch (error) {
      showToast(error.message, LABEL.TOAST_TYPE.ERROR);
    }
  };

  /**
   * Appends query parameters to the URL.
   * @param {Object} params - The parameters to append.
   */
  const appendQueryParam = (params) => {
    const searchParams = new URLSearchParams(location.search);
    for (const [key, value] of Object.entries(params)) {
      searchParams.set(key, value);
    }
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  /**
   * Handles uploading the bot image.
   * @param {Object} e - The event object.
   */
  const handleUplaodBotImage = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    const fileName = file.name?.toLowerCase?.() || "";
    const hasSupportedExtension = supportedImageExtensions.some((extension) =>
      fileName.endsWith(extension),
    );
    const hasSupportedMimeType = supportedImageTypes.includes(file.type);

    if (hasSupportedMimeType || hasSupportedExtension) {
      setBotImageFields((prev) => ({ ...prev, err: null }));
      setCropImageData({ src: URL.createObjectURL(file), file });
    } else {
      setBotImageFields((prev) => ({
        ...prev,
        err: botIconUploadErrorMessage,
      }));
      showToast(botIconUploadErrorMessage, LABEL.TOAST_TYPE.ERROR);
    }

    e.target.value = "";
  };

  return botStyleLoading ? (
    <SplashScreen />
  ) : (
    <div className="tab-content-grid">
      <div className="tab-content active">
        <div className="chatbot-history-grid">
          <header className="d-md-flex align-items-center justify-content-between">
            <h3>Appearance</h3>

            <div className="group-form d-md-flex">
              {Boolean(!!selectedTemplate && permission.edit) && (
                <button
                  onClick={() => setSelectedTemplate(null)}
                  className="cta-primary"
                >
                  <i className="fa-solid fa-angles-left fa-fade"></i> Back to
                  Chatbot Templates
                </button>
              )}
              {!selectedTemplate && !!bot_id && (
                <button
                  onClick={() => {
                    if (new URLSearchParams(location?.search).get("temp")) {
                      setSelectedTemplate(
                        "template#" +
                          new URLSearchParams(location?.search).get("temp"),
                      );
                    }
                  }}
                  className="cta-primary"
                >
                  Continue Editing
                </button>
              )}
              {Boolean(!!selectedTemplate && permission.edit) && (
                <button
                  // disabled={!!loader}
                  className="cta-primary"
                  onClick={() => handleSaveStep1()}
                >
                  {!!loader && <i className="fa-solid fa-spinner fa-spin"></i>}{" "}
                  Save and continue
                </button>
              )}
            </div>
          </header>
          {!selectedTemplate && (
            <p className="fw-bold text-danger fs-5">
              Please Select a Chat Template Bellow.
            </p>
          )}
          <div className="appearance-grid">
            {/* {Object.keys(components).map((key) => {
              const Component = components[key];
              return (
                <div className="dark-bg2 box-38">
                  <Component type={"appearance"}/>
                </div>
              );
            })} */}
            <Suspense fallback={<SplashScreen />}>
              <Templates
                type={"appearance"}
                selectedTemplate={selectedTemplate}
                setSelectedTemplate={setSelectedTemplate}
                botIcon={botImageFields.icon}
                colorSettingsArray={colorSettingsArray}
              />
            </Suspense>
            {!!selectedTemplate && (
              <div className="appearance-select-color white-bg2 box-24">
                <h3>Style</h3>
                {colorSettingsArray?.length > 0 &&
                  colorSettingsArray.map(
                    (settings, index) =>
                      ((selectedTemplate == "template#2" &&
                        settings.key != "bot_border_color" &&
                        settings.key != "sender_border_color") ||
                        (selectedTemplate == "template#1" &&
                          settings.key != "bot_tb_color" &&
                          settings.key != "sender_tb_color")) && (
                        <ColorPickerField
                          key={settings.key}
                          settings={settings}
                          index={index}
                          showIndex={showIndex}
                          setShowIndex={setShowIndex}
                          handleSetColor={handleSetColor}
                        />
                      ),
                  )}
              </div>
            )}
            {!!selectedTemplate && (
              <div className="appearance-select-color white-bg2 box-24">
                <div className="botname_block">
                  <h3>Bot Name *</h3>
                  <div
                    className={
                      !botNameFields.isUpdate
                        ? "botname-block-form active"
                        : "botname-block-form"
                    }
                  >
                    <input
                      // ref={inputRef}
                      value={botNameFields.name}
                      type="text"
                      // disabled={!botNameFields.isUpdate}
                      onChange={(e) => {
                        e.preventDefault();
                        setBotNameFields({
                          ...botNameFields,
                          name: !!e.target.value.trim() ? e.target.value : "",
                          err: null,
                        });
                      }}
                      onBlur={() =>
                        setBotNameFields({ ...botNameFields, isUpdate: false })
                      }
                    />
                    {Boolean(!botNameFields.isUpdate && permission.edit) && (
                      <span className="edit-btn">
                        <img
                          src="/images/icons/edit-icon.svg"
                          alt=""
                          // onClick={() =>
                          //   setBotNameFields({
                          //     ...botNameFields,
                          //     isUpdate: true,
                          //   })
                          // }
                        />
                      </span>
                    )}
                  </div>
                  {!!botNameFields.err && <span>{botNameFields.err}</span>}
                </div>
                <div className="boticon_wrap">
                  <h3>Bot Icon *</h3>
                  <div className="boticon_block">
                    {Boolean(botImageFields.icon && permission.edit) && (
                      <button
                        type="button"
                        className="reset-img"
                        onClick={resetIcon}
                        title="Delete bot icon"
                        aria-label="Delete bot icon"
                      >
                        <img src="/images/icons/delete-icon-red.svg" alt="" />
                      </button>
                    )}
                    <div
                      className={
                        !botNameFields.isUpdate
                          ? "botname-block-form active"
                          : "botname-block-form"
                      }
                      onClick={openBotImagePicker}
                    >
                      <div className="profile-photo_block">
                        <figure>
                          <img alt="avatar" src={botIconPreviewSrc} />
                        </figure>
                        <div className="file-upload-grid">
                          <img alt="avatar" src="/images/camera-solid.svg" />
                          {permission.edit && (
                            <input
                              type="file"
                              id="file-upload"
                              className="file-upload-input"
                              accept={supportedImageExtensions.join(",")}
                              ref={fileRef}
                              hidden
                              onChange={(e) => handleUplaodBotImage(e)}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    {!!botImageFields.err && (
                      <p className="text-danger small mt-2 mb-0">
                        {botImageFields.err}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          {Boolean(cropImageData.src) && (
            <div
              className="bot-icon-crop-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="bot-icon-crop-title"
            >
              <div className="bot-icon-crop-modal__panel">
                <div className="bot-icon-crop-modal__header">
                  <h3 id="bot-icon-crop-title">Crop bot icon</h3>
                  <button
                    type="button"
                    className="bot-icon-crop-modal__close"
                    onClick={closeBotIconCrop}
                    aria-label="Close crop dialog"
                  >
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                </div>
                <div className="bot-icon-crop-modal__body">
                  <ReactCrop
                    aspect={1}
                    circularCrop
                    crop={botIconCrop}
                    keepSelection
                    minWidth={40}
                    onChange={(_, percentCrop) => setBotIconCrop(percentCrop)}
                    onComplete={(pixelCrop) =>
                      setCompletedBotIconCrop(pixelCrop)
                    }
                  >
                    <img
                      ref={cropImageRef}
                      alt="Crop bot icon"
                      src={cropImageData.src}
                      onLoad={handleCropImageLoad}
                    />
                  </ReactCrop>
                </div>
                <div className="bot-icon-crop-modal__actions">
                  <button
                    type="button"
                    className="cta-secondary"
                    onClick={closeBotIconCrop}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="cta-primary"
                    disabled={!completedBotIconCrop?.width}
                    onClick={applyBotIconCrop}
                  >
                    Apply crop
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
